import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, Bot, Radio, BarChart3, HeadphonesIcon, Zap, Users, Lock, Globe, Settings } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: MessageSquare,
      title: 'WhatsApp Business API',
      description: 'Get official WhatsApp Business API access to send and receive messages at scale. Connect with customers on their preferred messaging platform.',
      features: [
        'Official WhatsApp verification',
        'Green tick badge',
        'Unlimited messaging capacity',
        'Multi-device support',
        'Rich media messaging'
      ]
    },
    {
      icon: Bot,
      title: 'Chatbot Integration',
      description: 'Build intelligent chatbots with drag-and-drop functionality. Automate customer service, lead generation, and sales conversations 24/7.',
      features: [
        'Visual flow builder',
        'AI-powered responses',
        'Natural language processing',
        'Multi-language support',
        'Context-aware conversations'
      ]
    },
    {
      icon: Radio,
      title: 'Broadcasting & Campaigns',
      description: 'Send targeted broadcast messages to thousands of customers. Create personalized campaigns with smart segmentation and scheduling.',
      features: [
        'Audience segmentation',
        'Campaign scheduler',
        'Template management',
        'Click tracking',
        'A/B testing'
      ]
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Get detailed insights into your WhatsApp communications. Track message delivery, engagement rates, and customer behavior patterns.',
      features: [
        'Real-time dashboards',
        'Message analytics',
        'User engagement metrics',
        'Custom reports',
        'Export capabilities'
      ]
    },
    {
      icon: HeadphonesIcon,
      title: 'Multi-Agent Support',
      description: 'Enable your support team to handle multiple conversations simultaneously. Assign chats, transfer between agents, and monitor performance.',
      features: [
        'Shared team inbox',
        'Agent routing',
        'Internal notes',
        'Performance monitoring',
        'SLA management'
      ]
    },
    {
      icon: Zap,
      title: 'Integrations',
      description: 'Seamlessly connect WhatsApp with your existing tools. Integrate with CRMs, e-commerce platforms, and business applications.',
      features: [
        'Shopify & WooCommerce',
        'Salesforce & HubSpot',
        'Zapier integration',
        'Custom API access',
        'Webhook support'
      ]
    },
    {
      icon: Users,
      title: 'Contact Management',
      description: 'Organize and manage your customer contacts efficiently. Create custom attributes, tags, and segments for targeted messaging.',
      features: [
        'Unlimited contacts',
        'Custom fields & tags',
        'Import/Export contacts',
        'Duplicate detection',
        'Contact enrichment'
      ]
    },
    {
      icon: Lock,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security with end-to-end encryption. Ensure compliance with GDPR, data protection, and privacy regulations.',
      features: [
        'End-to-end encryption',
        'GDPR compliant',
        'Data residency options',
        'Access controls',
        'Audit logs'
      ]
    },
    {
      icon: Globe,
      title: 'Global Messaging',
      description: 'Reach customers worldwide with support for 180+ countries. Benefit from competitive international messaging rates.',
      features: [
        '180+ countries supported',
        'Multi-language templates',
        'Local number provisioning',
        'Currency support',
        'Regional compliance'
      ]
    },
    {
      icon: Settings,
      title: 'Custom Development',
      description: 'Need something unique? Our team can build custom solutions tailored to your specific business requirements and workflows.',
      features: [
        'Custom integrations',
        'Workflow automation',
        'API development',
        'White-label solutions',
        'Dedicated support'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            MNV Solutions provides comprehensive WhatsApp Business solutions to help you connect, engage, and grow your business
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <Card key={idx} className="hover:shadow-xl transition-all duration-300 border-gray-200">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="space-y-2 mb-6">
                      <h4 className="font-semibold text-gray-900 text-sm">Key Features:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-start gap-2 text-sm text-gray-700">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 flex-shrink-0"></div>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Choose a plan that fits your needs or contact us for a custom solution
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
              <Link to="/#pricing">View Pricing</Link>
            </Button>
            <Button asChild variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
              <Link to="/#contact">Book a Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
